import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import { Field, useFormikContext } from "formik";
import { TextField } from "formik-mui";
import * as yup from "yup";

export interface NoticeDetailsValues {
  violationCode: string;
  location: string;
  officerNotes: string;
}

export interface ViolationTypeOption {
  /** Already in "{code} — {description}" display form -- shown as the MenuItem's text and
   *  passed to PrefilledDetails to echo, but never stored in the violationCode field itself
   *  (see policyId below) since two policies could share an identical code+description. */
  label: string;
  /** enforcement.policy id -- submitted as enforcementPolicyId when creating the intent, and
   *  stored in the violationCode field as the Select's value. Using the id rather than the
   *  display label as the field's value/identity means two violation types that happened to
   *  render identically could never resolve to the wrong policy. */
  policyId: string;
  /** Fine amount in cents, for ViolationPenalty's live total. */
  amountDueCents: number;
}

export interface NoticeDetailsProps {
  /** Selectable violation codes, sourced from the violation-types endpoint (see AUT-5574) via
   *  useViolationTypes -- see that hook for the fetch itself. */
  violationTypeOptions?: ViolationTypeOption[];
  /** True while the violation-types request is still in flight, so this section can show a
   *  loading state distinct from "confirmed empty, nothing configured for this jurisdiction" --
   *  without this, a slow network looks identical to a real configuration gap for however long
   *  the request takes. */
  violationTypesLoading?: boolean;
  /** Set when the violation-types request itself failed (e.g. a transient API/network error),
   *  as opposed to succeeding with a genuinely empty list. Without this, both cases look
   *  identical -- an Apollo error resolves `violationTypeOptions` to an empty array with
   *  `loading: false`, which is indistinguishable from a jurisdiction with nothing configured --
   *  and the officer is told to "contact your administrator" over what may just be a transient
   *  failure worth retrying instead. */
  violationTypesError?: Error | null;
  /** Re-runs the violation-types request. Only rendered as a "Retry" action alongside
   *  `violationTypesError` -- without an actual way to retry, telling the officer to "try
   *  again" is meaningless, since Clear Form doesn't rerun this request and there's no other
   *  path back to a working form short of leaving and reopening the route. */
  onRetryViolationTypes?: () => void;
}

export interface NoticeDetailsComponent extends React.FC<NoticeDetailsProps> {
  defaultInitialValues: NoticeDetailsValues;
  validationSchema: yup.AnyObjectSchema;
}

export const NoticeDetails: NoticeDetailsComponent = ({
  violationTypeOptions,
  violationTypesLoading,
  violationTypesError,
  onRetryViolationTypes,
}) => {
  const { values, setFieldValue } = useFormikContext<NoticeDetailsValues>();

  // An effect rather than an initial value: options load after Formik mounts, and this also refills after Clear Form.
  useEffect(() => {
    if (violationTypeOptions?.length === 1 && values.violationCode === "") {
      setFieldValue("violationCode", violationTypeOptions[0].policyId);
    }
  }, [violationTypeOptions, values.violationCode, setFieldValue]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Notice Details
      </Typography>
      {violationTypesLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading violation types…
        </Typography>
      ) : violationTypesError ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="error">
            Failed to load violation types. Check your connection and try again.
          </Typography>
          <Button size="small" onClick={onRetryViolationTypes}>
            Retry
          </Button>
        </Stack>
      ) : !violationTypeOptions || violationTypeOptions.length === 0 ? (
        // Exact wording from AUT-5575's Business Logic table (Part B, Error handling), not
        // paraphrased -- this is a specified error state, not a placeholder we get to word freely.
        <Typography variant="body2" color="text.secondary">
          No violation types configured for your jurisdiction, contact your administrator.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Field component={TextField} select fullWidth required name="violationCode" label="Violation Code">
              {violationTypeOptions.map((option) => (
                <MenuItem key={option.policyId} value={option.policyId}>
                  {option.label}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field component={TextField} fullWidth required name="location" label="Location" />
          </Grid>
          <Grid item xs={12}>
            <Field
              component={TextField}
              fullWidth
              multiline
              minRows={3}
              name="officerNotes"
              label="Officer Notes (optional)"
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

NoticeDetails.defaultInitialValues = {
  violationCode: "",
  location: "",
  officerNotes: "",
};

// Messages match the copy this section/GenerateCitation already showed via manual onSubmit
// checks before this schema existed -- keeping them prevents the visible error text from
// changing for officers even though the enforcement mechanism did.
NoticeDetails.validationSchema = yup.object({
  violationCode: yup.string().required("Select a violation code before submitting."),
  // .trim() as a validation test (not a transform -- a transform would validate a trimmed copy
  // without actually cleaning the submitted value) rejects a whitespace-only value, since trimming
  // it no longer equals the original -- .required() alone accepts "   " as non-empty.
  location: yup.string().trim("Enter a location before submitting.").required("Enter a location before submitting."),
  officerNotes: yup.string(),
});

export default NoticeDetails;
